/**
 * A DISPUTA DE ESPAÇO entre os pinos, sem DOM.
 *
 * Mora fora do componente do mapa porque é a única parte do declutter que é
 * geometria pura — e a única testável: dentro do mapa ela depende de larguras
 * medidas, que em jsdom são zero, e a rotina inteira degrada para no-op. Quem
 * mede continua sendo o mapa; quem decide é isto aqui.
 */

/** A caixa da pílula em px, derivada do CSS (corpo de 28px suspenso 8px acima
 *  da coordenada). Não se mede a caixa a cada zoom porque o corpo rebaixado
 *  está em `scale(0.35)` e mentiria o tamanho — só a LARGURA é medida, e uma
 *  vez. Os 4px de folga separam pílulas coladas e absorvem a variação de
 *  largura quando a fonte termina de carregar. */
const PIN_BODY_HEIGHT = 28;
const PIN_BODY_LIFT = 8;
const PIN_BODY_GUTTER = 4;

export type PinBox = { x1: number; y1: number; x2: number; y2: number };

export function pinBox(point: { x: number; y: number }, width: number): PinBox {
  return {
    x1: point.x - width / 2 - PIN_BODY_GUTTER,
    y1: point.y - PIN_BODY_LIFT - PIN_BODY_HEIGHT - PIN_BODY_GUTTER,
    x2: point.x + width / 2 + PIN_BODY_GUTTER,
    y2: point.y - PIN_BODY_LIFT + PIN_BODY_GUTTER,
  };
}

function overlaps(a: PinBox, b: PinBox): boolean {
  return a.x1 < b.x2 && a.x2 > b.x1 && a.y1 < b.y2 && a.y2 > b.y1;
}

/**
 * A FORÇA na disputa, e ela é o eixo do selo.
 *
 * A ordem da lista não serve como fila: no mapa cheio a lista é a ordenação
 * que a pessoa escolheu (preço, distância, nota), e aí o hotel que ordenou na
 * frente rebaixa o **Recomendado** a ponto de 10px — o mapa esconde justamente
 * a resposta que o agente deu. Quem chega antes é quem tem o que dizer.
 *
 * Só o eixo entra: dentro de cada faixa vale a ordem que veio, que já é a
 * curadoria de quem montou a lista.
 *
 * `stayed` (o selo pessoal "você ficou aqui") disputa na faixa do recomendado:
 * a memória do viajante não pode ser rebaixada pelo hotel que ordenou na
 * frente; dentro da faixa, a ordem que veio decide (o produtor já põe os
 * já-ficou primeiro).
 *
 * `favorite` (o coração, item 1 F2) fica ACIMA de todos, inclusive de
 * `stayed`: estadia é registro do que aconteceu, favorito é a escolha que o
 * viajante declarou — no mapa apertado, é o pino que ele veio procurar.
 *
 * `lastBooked` (the car's "Última reserva", #5529) never reaches a hotel pin;
 * it sits with `stayed` because it is the same kind of personal memory.
 *
 * `agreement` (o hotel com acordo da empresa, #6359) disputa na faixa do
 * recomendado: é o mesmo selo sólido no card.
 */
const PRIORITY = {
  favorite: 0,
  recommended: 1,
  stayed: 1,
  lastBooked: 1,
  agreement: 1,
  cheapest: 2,
  closest: 2,
} as const;

export type PinAxis = keyof typeof PRIORITY;

export type PinCandidate = {
  readonly id: string;
  readonly point: { x: number; y: number };
  /** A pílula inteira: `{glifo} {preço} · {nome curto} {escudo}`. */
  readonly fullWidth: number;
  /** A mesma pílula sem o nome curto. Nunca maior que `fullWidth`. */
  readonly shortWidth: number;
  readonly axis?: PinAxis;
};

/** `full` mostra preço e nome; `short` só o preço; `stacked` é o preço ATRÁS de
 *  quem ganhou o espaço. Não existe degrau abaixo destes: **nenhum pino perde
 *  o preço.** */
export type PinPlacement = 'full' | 'short' | 'stacked';

/**
 * Distribui o espaço e devolve o que cada pino vira.
 *
 * **O piso é o preço, não o ponto.** O ponto de 10px que existia aqui era o
 * padrão de mercado e resolvia a colisão pelo lado errado: ele apagava
 * justamente o número que a pessoa veio comparar, e num aglomerado — que é
 * quando ele aparece — apagava metade do mapa. Quem perde a disputa agora
 * mantém a pílula do preço e vai para TRÁS de quem ganhou: fica coberto em
 * parte, nunca apagado, e hover ou seleção o trazem à frente.
 *
 * Os dois degraus continuam: pílula inteira, pílula sem o nome curto, e então
 * a mesma pílula curta em segundo plano. O nome é o que serve para comparar, e
 * comparar é o que a pessoa faz depois de já ter reparado no preço.
 *
 * `reserved` são as caixas que entram pré-aceitas — hoje só a âncora do
 * compromisso, que é a régua de onde a distância é contada: um mapa que
 * esconde a régua não mede nada.
 */
export function resolvePinPlacements(
  candidates: readonly PinCandidate[],
  reserved: readonly PinBox[] = [],
): Map<string, PinPlacement> {
  const taken = [...reserved];
  const placements = new Map<string, PinPlacement>();
  const queue = candidates
    .map((candidate, index) => ({ candidate, index }))
    .sort((a, b) => priorityOf(a.candidate) - priorityOf(b.candidate) || a.index - b.index);

  for (const { candidate } of queue) {
    const full = pinBox(candidate.point, candidate.fullWidth);
    if (!taken.some((other) => overlaps(full, other))) {
      taken.push(full);
      placements.set(candidate.id, 'full');
      continue;
    }
    const short = pinBox(candidate.point, candidate.shortWidth);
    // Só vale tentar se a pílula curta for MESMO menor: sem nome para esconder
    // (nome curto de uma sílaba, pino sem nome) as duas caixas são a mesma, e
    // repetir a conta devolveria o mesmo choque.
    if (
      candidate.shortWidth < candidate.fullWidth &&
      !taken.some((other) => overlaps(short, other))
    ) {
      taken.push(short);
      placements.set(candidate.id, 'short');
      continue;
    }
    placements.set(candidate.id, 'stacked');
  }
  return placements;
}

function priorityOf(candidate: PinCandidate): number {
  return candidate.axis ? PRIORITY[candidate.axis] : 3;
}
